import { useState } from "react"
import { Tag, X, Plus } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"

interface BulkTagDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  count: number
  onConfirm: (tagNames: string[]) => Promise<void>
}

export function BulkTagDialog({
  open,
  onOpenChange,
  count,
  onConfirm,
}: BulkTagDialogProps) {
  const [tagInput, setTagInput] = useState("")
  const [tags, setTags] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const addTag = () => {
    const trimmed = tagInput.trim()
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed])
      setTagInput("")
    }
  }

  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      addTag()
    }
  }

  const handleConfirm = async () => {
    if (tags.length === 0) return
    setIsSubmitting(true)
    try {
      await onConfirm(tags)
      onOpenChange(false)
      setTags([])
      setTagInput("")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleOpenChange = (val: boolean) => {
    onOpenChange(val)
    if (!val) {
      setTags([])
      setTagInput("")
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag size={20} className="text-primary" />
            Dodaj tagi
          </DialogTitle>
          <DialogDescription>
            Dodaj tagi do {count}{" "}
            {count === 1 ? "elementu" : "elementow"}.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Wpisz nazwe tagu..."
              className="h-9 flex-1 rounded-lg border border-input bg-transparent px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addTag}
              disabled={!tagInput.trim()}
            >
              <Plus size={14} />
            </Button>
          </div>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="gap-1 pl-2 pr-1 text-xs"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="rounded-full p-0.5 hover:bg-black/10"
                  >
                    <X size={12} />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            Anuluj
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting || tags.length === 0}
          >
            {isSubmitting ? "Zapisywanie..." : `Dodaj tagi (${tags.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
